import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { Avatar, Link } from '@/components/atoms';
import { dropdownStyles, getIndicatorMargin } from '@/lib/dropdownStyles';
import { cn } from '@/lib/utils';
import { useTheme } from '@/theme';
import { type User } from '@/types';
import { userProfileUrl, userPhotosUrl } from '@/utils/urls';

export interface UserControlsProps {
  user: User | null;
  onLogout: () => void;
  className?: string;
}

/**
 * UserControls - User avatar with dropdown menu
 *
 * Features:
 * - Avatar display with fallback initials
 * - Dropdown menu with user actions (Profile, Settings, Logout)
 * - Theme switcher (Light, Dark, Auto)
 * - Login/Register buttons when not authenticated
 *
 * Usage:
 *   <UserControls user={user} onLogout={handleLogout} />
 */
export function UserControls({ user, onLogout, className }: UserControlsProps) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  // Unauthenticated state: Show login button only (registration is invite-only)
  // Feature: 647-invite-registration
  if (!user) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Link to="/login" variant="link-primary" className="text-foreground hover:text-primary">
          Log In
        </Link>
        <span className="text-muted text-sm" title="Registration is invitation only">
          Invitation Only
        </span>
      </div>
    );
  }

  // Authenticated state: Show avatar with dropdown menu
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          data-testid="user-menu"
          className={cn('flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500', className)}
        >
          <Avatar alt={user.username} fallback={user.username} size="sm" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownStyles.content} sideOffset={5}>
          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate(userProfileUrl(user.slug))}
          >
            Profile
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate(userPhotosUrl(user.slug))}
          >
            My Photos
          </DropdownMenu.Item>

          <DropdownMenu.Separator className={dropdownStyles.separator} />

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate('/about')}
          >
            About
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate('/privacy')}
          >
            Privacy
          </DropdownMenu.Item>

          <DropdownMenu.Separator className={dropdownStyles.separator} />

          {/* Theme Switcher */}
          <DropdownMenu.Label className={dropdownStyles.label}>Theme</DropdownMenu.Label>

          <DropdownMenu.RadioGroup value={theme} onValueChange={(value) => setTheme(value as 'light' | 'dark' | 'auto')}>
            <DropdownMenu.RadioItem value="light" className={dropdownStyles.itemWithIndicator}>
              <DropdownMenu.ItemIndicator className={dropdownStyles.indicator}>
                <span className={dropdownStyles.radioIndicator}></span>
              </DropdownMenu.ItemIndicator>
              <span className={getIndicatorMargin(theme === 'light')}>☀️ Light</span>
            </DropdownMenu.RadioItem>

            <DropdownMenu.RadioItem value="dark" className={dropdownStyles.itemWithIndicator}>
              <DropdownMenu.ItemIndicator className={dropdownStyles.indicator}>
                <span className={dropdownStyles.radioIndicator}></span>
              </DropdownMenu.ItemIndicator>
              <span className={getIndicatorMargin(theme === 'dark')}>🌙 Dark</span>
            </DropdownMenu.RadioItem>

            <DropdownMenu.RadioItem value="auto" className={dropdownStyles.itemWithIndicator}>
              <DropdownMenu.ItemIndicator className={dropdownStyles.indicator}>
                <span className={dropdownStyles.radioIndicator}></span>
              </DropdownMenu.ItemIndicator>
              <span className={getIndicatorMargin(theme === 'auto')}>🔄 Auto</span>
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className={dropdownStyles.separator} />

          <DropdownMenu.Item className={dropdownStyles.itemDanger} onSelect={onLogout}>
            Log Out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
